/**
 * Integer-cent money. No floats ever touch a monetary value.
 *
 * `Cents` is branded so a raw `number` cannot be passed where money is
 * expected — the compiler forces every value through `cents()`, which is the
 * single place where "is this actually an integer?" is checked.
 */

declare const CentsBrand: unique symbol;
export type Cents = number & { readonly [CentsBrand]: true };

export const ZERO = 0 as Cents;

export function cents(n: number): Cents {
  if (!Number.isInteger(n)) {
    throw new RangeError(`money must be whole cents, got ${n}`);
  }
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`money out of safe integer range: ${n}`);
  }
  return n as Cents;
}

/** Build cents from a major-unit amount, e.g. fromMajor(12.5) -> 1250. */
export function fromMajor(amount: number): Cents {
  return cents(Math.round(amount * 100));
}

export const add = (a: Cents, b: Cents): Cents => cents(a + b);
export const sub = (a: Cents, b: Cents): Cents => cents(a - b);
export const mul = (a: Cents, factor: number): Cents => cents(a * factor);
export const sum = (xs: readonly Cents[]): Cents =>
  cents(xs.reduce<number>((acc, x) => acc + x, 0));
export const abs = (a: Cents): Cents => cents(Math.abs(a));

const SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
};

/** Human-readable amount. Symbol currencies prefix, everything else suffixes its code. */
export function formatMoney(amount: Cents, currency: string): string {
  const negative = amount < 0;
  const magnitude = (Math.abs(amount) / 100).toFixed(2);
  const symbol = SYMBOLS[currency.toUpperCase()];
  const body = symbol ? `${symbol}${magnitude}` : `${magnitude} ${currency}`;
  return negative ? `-${body}` : body;
}

/**
 * Split `total` across `weights` so the parts sum to exactly `total`.
 *
 * Uses the largest-remainder (Hamilton) method: each part gets its floor, then
 * leftover pennies go to the largest fractional remainders, ties broken by
 * index. That tiebreak is what makes the result deterministic — the same bill
 * always splits the same way, which matters when people re-open a share link
 * and compare.
 *
 * Arithmetic is done in BigInt because `total * weight` can exceed 2^53 once
 * weights are themselves cent amounts (as they are for proportional tax).
 */
export function distribute(total: Cents, weights: readonly number[]): Cents[] {
  if (weights.length === 0) return [];
  for (const w of weights) {
    if (!Number.isInteger(w) || w < 0) {
      throw new RangeError(`weights must be non-negative integers, got ${w}`);
    }
  }

  const weightSum = weights.reduce((a, b) => a + b, 0);
  if (weightSum === 0) {
    throw new RangeError('cannot distribute across zero total weight');
  }
  if (total === 0) return weights.map(() => ZERO);

  // Work on the magnitude so BigInt truncation is a true floor, then re-apply
  // the sign. Negative totals are real: discounts and voided items.
  const negative = total < 0;
  const magnitude = BigInt(Math.abs(total));
  const denominator = BigInt(weightSum);

  const quotients: bigint[] = [];
  const remainders: bigint[] = [];
  for (const w of weights) {
    const numerator = magnitude * BigInt(w);
    quotients.push(numerator / denominator);
    remainders.push(numerator % denominator);
  }

  let leftover = Number(magnitude - quotients.reduce((a, b) => a + b, 0n));
  const order = remainders
    .map((remainder, index) => ({ remainder, index }))
    .sort((a, b) => (b.remainder === a.remainder ? a.index - b.index : b.remainder > a.remainder ? 1 : -1));

  const parts = quotients.map((q) => Number(q));
  for (const { index } of order) {
    if (leftover <= 0) break;
    parts[index] = (parts[index] ?? 0) + 1;
    leftover -= 1;
  }

  return parts.map((p) => cents(negative ? -p : p));
}

/** Convenience: split evenly across `n` shares. */
export function distributeEvenly(total: Cents, n: number): Cents[] {
  return distribute(total, new Array<number>(n).fill(1));
}
